export type UnitType = 'row' | 'column' | 'box';

export type PeerUnitCells = {
  rowCells: number[];
  columnCells: number[];
  boxCells: number[];
};

export type BoardHighlightTargets = PeerUnitCells & {
  selectedCellIndex: number | null;
  selectedDigit: number | null;
  sameDigitCells: number[];
};

export type CompletedUnit = {
  type: UnitType;
  index: number;
  cells: number[];
};

export type UnitCompletionInput = {
  beforeBoard: string[];
  afterBoard: string[];
  placedCellIndex: number;
};

export type Key9UnlockCelebrationInput = {
  keyCells: number[];
  usedTileCount: number;
  totalTiles: number;
};

export type Key9UnlockCelebration = {
  play: boolean;
  cells: number[];
  tileIndices: number[];
};

const COMPLETE_DIGITS = '123456789';

export function getPeerUnitCells(cellIndex: number): PeerUnitCells {
  const row = Math.floor(cellIndex / 9);
  const col = cellIndex % 9;
  const boxRow = Math.floor(row / 3) * 3;
  const boxCol = Math.floor(col / 3) * 3;

  const rowCells = Array.from({ length: 9 }, (_, index) => row * 9 + index);
  const columnCells = Array.from({ length: 9 }, (_, index) => index * 9 + col);
  const boxCells = Array.from({ length: 9 }, (_, index) => {
    const r = boxRow + Math.floor(index / 3);
    const c = boxCol + (index % 3);
    return r * 9 + c;
  });

  return { rowCells, columnCells, boxCells };
}

export function getSameDigitCells(board: string[], digit: number | string | null | undefined): number[] {
  const normalized = normalizeDigit(digit);
  if (normalized == null) return [];
  return board
    .map((value, index) => (value === String(normalized) ? index : -1))
    .filter((index) => index >= 0);
}

export function getBoardHighlightTargets(input: {
  board: string[];
  selectedCellIndex?: number | null;
  selectedDigit?: number | string | null;
}): BoardHighlightTargets {
  const selectedCellIndex = isCellIndex(input.selectedCellIndex) ? input.selectedCellIndex : null;
  const selectedDigit = normalizeDigit(input.selectedDigit);
  const peers = selectedCellIndex == null
    ? { rowCells: [], columnCells: [], boxCells: [] }
    : getPeerUnitCells(selectedCellIndex);
  const digitFromCell = selectedCellIndex == null ? null : normalizeDigit(input.board[selectedCellIndex]);
  const highlightDigit = selectedDigit ?? digitFromCell;

  return {
    selectedCellIndex,
    selectedDigit: highlightDigit,
    ...peers,
    sameDigitCells: getSameDigitCells(input.board, highlightDigit)
  };
}

export function detectNewlyCompletedUnits(input: UnitCompletionInput): CompletedUnit[] {
  if (!isCellIndex(input.placedCellIndex)) return [];
  const row = Math.floor(input.placedCellIndex / 9);
  const col = input.placedCellIndex % 9;
  const box = Math.floor(row / 3) * 3 + Math.floor(col / 3);
  const candidates: CompletedUnit[] = [
    { type: 'row', index: row, cells: getRowCells(row) },
    { type: 'column', index: col, cells: getColumnCells(col) },
    { type: 'box', index: box, cells: getBoxCells(box) }
  ];

  return candidates.filter((unit) => (
    !isCompleteValidUnit(input.beforeBoard, unit.cells)
    && isCompleteValidUnit(input.afterBoard, unit.cells)
  ));
}

export function getKey9UnlockCelebration(input: Key9UnlockCelebrationInput): Key9UnlockCelebration {
  const play = input.totalTiles > 0 && input.usedTileCount >= input.totalTiles;
  return {
    play,
    cells: play ? [...input.keyCells] : [],
    tileIndices: play ? Array.from({ length: input.totalTiles }, (_, index) => index) : []
  };
}

export function isCompleteValidUnit(board: string[], cells: number[]): boolean {
  const values = cells.map((cell) => board[cell] ?? '0');
  if (values.some((value) => !/^[1-9]$/.test(value))) return false;
  return values.slice().sort().join('') === COMPLETE_DIGITS;
}

function getRowCells(row: number): number[] {
  return Array.from({ length: 9 }, (_, index) => row * 9 + index);
}

function getColumnCells(col: number): number[] {
  return Array.from({ length: 9 }, (_, index) => index * 9 + col);
}

function getBoxCells(box: number): number[] {
  const boxRow = Math.floor(box / 3) * 3;
  const boxCol = (box % 3) * 3;
  return Array.from({ length: 9 }, (_, index) => {
    const r = boxRow + Math.floor(index / 3);
    const c = boxCol + (index % 3);
    return r * 9 + c;
  });
}

function isCellIndex(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < 81;
}

function normalizeDigit(value: number | string | null | undefined): number | null {
  const digit = Number(value);
  return Number.isInteger(digit) && digit >= 1 && digit <= 9 ? digit : null;
}
